import {Tabs, TabsProps} from 'antd';

import {AlertFallback} from '../../../../component/alert-fallback/alert-fallback';
import {CursorPaginator} from '../../../../component/cursor-paginator/cursor-paginator';
import {PageCard} from '../../../../layout/page-card/page-card';
import {Locale} from '../../../../provider/locale/locale';
import {LangKeyType} from '../../../../provider/locale/translation/type';
import {useEvents} from '../../../../service/events/events-hook';
import {PostListItemType} from '../../../../service/posts/posts-types';
import {usePromotions} from '../../../../service/promotions/promotions-hook';
import {PublicationListFilterType} from '../../../../service/publication/publication-filter-type';
import {useYandexStories} from '../../../../service/yandex-stories/yandex-stories-hook';
import {CursorPaginationHookType} from '../../../../util/cursor-pagination/cursor-pagination-type';
import {getEnumValueEnsure} from '../../../../util/enum';
import {productGroupsSizeOptions} from '../../price/products-const';
import {EventsTable} from '../events-table/events-table';
import {PostsTabEnum} from '../posts-const';
import {PostsTable} from '../posts-table/posts-table';
import {PromotionsTable} from '../promotions-table/promotions-table';
import {StoriesTable} from '../stories-table/stories-table';

type PostsTableCardPropsType = {
    activeTab: PostsTabEnum;
    areCatalogTabsVisible: boolean;
    isLicenseExpired: boolean;
    posts: Array<PostListItemType>;
    postsCount?: number | null;
    isInProgress: boolean;
    processError: boolean;
    refresh: VoidFunction;
    cursorPagination: CursorPaginationHookType;
    onTabChange: (tab: PostsTabEnum) => void;
    publicationFilter: PublicationListFilterType;
};

function getTabLabel(stringKey: LangKeyType, count?: number | null): JSX.Element {
    return (
        <span>
            <Locale stringKey={stringKey} />
            {typeof count === 'number' ? ` (${count})` : ''}
        </span>
    );
}

type TabCountsType = Partial<Record<PostsTabEnum, number | null>>;

const TAB_DEFINITIONS: Array<{key: PostsTabEnum; stringKey: LangKeyType}> = [
    {key: PostsTabEnum.Posts, stringKey: 'POSTS__TAB__POSTS'},
    {key: PostsTabEnum.Stories, stringKey: 'POSTS__TAB__STORIES'},
    {key: PostsTabEnum.Promotions, stringKey: 'POSTS__TAB__PROMOTIONS'},
    {key: PostsTabEnum.Events, stringKey: 'POSTS__TAB__EVENTS'},
];

// rc-tabs measures the underline width only when the set of tab keys changes (and on container
// resize), never when a label's text changes. The active tab's `(count)` is appended after that
// measurement, so we fold the count into that tab's key: when the count arrives the key changes,
// forcing a re-measure so the underline spans the full label. The nav stays mounted, so switching
// still slides the underline smoothly from the previous tab. The separator never appears in a base
// key, so it is stripped back out in onChange / matched in activeKey.
const TAB_KEY_SEPARATOR = '#';

function buildTabKey(key: PostsTabEnum, count?: number | null): string {
    return typeof count === 'number' ? `${key}${TAB_KEY_SEPARATOR}${count}` : key;
}

// Show the counter only for the active tab — other tabs haven't been fetched (or hold stale cached data).
function getTabItems(
    activeTab: PostsTabEnum,
    counts: TabCountsType,
    definitions: typeof TAB_DEFINITIONS
): TabsProps['items'] {
    return definitions.map(({key, stringKey}) => {
        const count = key === activeTab ? counts[key] : null;

        return {key: buildTabKey(key, count), label: getTabLabel(stringKey, count)};
    });
}

export function PostsTableCard(props: PostsTableCardPropsType): JSX.Element | null {
    const {
        activeTab,
        areCatalogTabsVisible,
        isLicenseExpired,
        posts,
        postsCount,
        isInProgress,
        processError,
        refresh,
        cursorPagination,
        onTabChange,
        publicationFilter,
    } = props;

    // Only the active tab fetches its list; cached results are reused on tab switches (staleTime: Infinity).
    const {data: storiesData} = useYandexStories(publicationFilter, {
        enabled: activeTab === PostsTabEnum.Stories,
        staleTime: Number.POSITIVE_INFINITY,
    });
    const {data: promotionsData} = usePromotions(publicationFilter, {
        enabled: activeTab === PostsTabEnum.Promotions,
        staleTime: Number.POSITIVE_INFINITY,
    });
    const {data: eventsData} = useEvents(publicationFilter, {
        enabled: activeTab === PostsTabEnum.Events,
        staleTime: Number.POSITIVE_INFINITY,
    });

    const tabCounts: TabCountsType = {
        posts: postsCount,
        stories: storiesData?.pages[0]?.count,
        promotions: promotionsData?.pages[0]?.count,
        events: eventsData?.pages[0]?.count,
    };

    // Without the publication-catalog flag only Posts is available; the other catalog tabs are hidden.
    const visibleTabDefinitions = areCatalogTabsVisible
        ? TAB_DEFINITIONS
        : TAB_DEFINITIONS.filter(({key}) => key === PostsTabEnum.Posts);

    const tabItems = getTabItems(activeTab, tabCounts, visibleTabDefinitions);

    // Must match the active item's key, which folds in the count (see getTabItems / buildTabKey).
    const activeKey = buildTabKey(activeTab, tabCounts[activeTab]);

    function handleTabChange(key: string): void {
        const separatorIndex = key.indexOf(TAB_KEY_SEPARATOR);
        const baseKey = separatorIndex === -1 ? key : key.slice(0, separatorIndex);

        onTabChange(getEnumValueEnsure(PostsTabEnum, baseKey, PostsTabEnum.Posts));
    }

    return (
        <>
            {processError && !isLicenseExpired && <AlertFallback />}

            {!isLicenseExpired && (
                <PageCard>
                    <Tabs activeKey={activeKey} items={tabItems} onChange={handleTabChange} />
                    {activeTab === PostsTabEnum.Posts && (
                        <>
                            <PostsTable datasource={posts} isLoadingData={isInProgress} onActionCompleted={refresh} />
                            <CursorPaginator
                                cursorPagination={cursorPagination}
                                pageSizeOptions={productGroupsSizeOptions}
                            />
                        </>
                    )}
                    {activeTab === PostsTabEnum.Stories && <StoriesTable filter={publicationFilter} />}
                    {activeTab === PostsTabEnum.Promotions && <PromotionsTable filter={publicationFilter} />}
                    {activeTab === PostsTabEnum.Events && <EventsTable filter={publicationFilter} />}
                </PageCard>
            )}
        </>
    );
}
